import { Controller, Loader } from '@tencent/workbench';
import { FORM_CONFIGS } from '@framework/workbench/drawer/ui/chart/global-settings/chart-fill/config';
import { FillType, LINEAR_GRADIENT, RADIAL_GRADIENT } from '@framework/workbench/drawer/ui/fill/constants';
import { Form, DragAngleCircleType } from '@tencent/wb-ext-form';
import { ChartFormItemKey } from '@tencent/chart-drawer-components/lib/schema';
import type { NumberInputFrom, AngleFrom } from '@tencent/wb-ext-form';
import type { IDynamicLoader } from '@tencent/workbench';
import { IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@src/framework/workbench/drawer/constants';
import { ANGLE_MAX } from '@src/framework/workbench/drawer/common/config';
import { IChartFillFeature } from './chart-fill-global-interface';

@Controller({ formItems: FORM_CONFIGS })
export class ChartFillGlobalController {
    constructor(@Loader(IChartFillFeature) private loader: IDynamicLoader<IChartFillFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_FILL_SELECT, 'value')
    public getFillSelectValue(): string {
        this.feature.updateUIWithFillBgData();
        if (typeof this.feature.curFillType === 'undefined') {
            this.feature.curFillType = FillType.NO_FILL;
        }
        return this.feature.curFillType;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_FILL_SELECT, 'options')
    public getOptions() {
        return {
            selectOptions: this.feature.getFillTypes(),
        };
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_FILL_SELECT, 'onChange')
    public changeFillSelectValue(value: string) {
        this.feature.curFillType = value;
        this.feature.handleFillStyleChange();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_SOLID_FILL_COLOR, 'content')
    public getColorSelectButton(): JSX.Element {
        this.feature.updateUIWithFillBgData();
        return this.feature.getColorSelectButton();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_SOLID_FILL_OPACITY, 'value')
    public getSolidFillOpacity(): number | IMultiDiffValue {
        const solidOpacityObj = { solidOpacity: this.feature.solidFill.getSolidOpacity() };
        return this.feature.getShownValue(this.feature.getInputParams().key, solidOpacityObj);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_SOLID_FILL_OPACITY, 'onChange')
    public changeSolidFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { solidOpacity } = this.feature.getValidValue({ solidOpacity: value }, from);
        this.feature.handleSolidOpacityChange(solidOpacity, from, dragEnd);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_LINEAR_GRADIENT, 'onClick')
    public changeGradualFillLinear() {
        this.feature.gradFill.value.curGradualType = LINEAR_GRADIENT;
        this.feature.gradFill.value.presetItem = null;
        this.feature.gradFill.confirmGradChoice();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_LINEAR_GRADIENT, 'value')
    public getGradualFillLinear(): boolean {
        return this.feature.gradFill.value.curGradualType === LINEAR_GRADIENT;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_RADIAL_GRADIENT, 'onClick')
    public changeGradualFillRadial() {
        this.feature.gradFill.value.curGradualType = RADIAL_GRADIENT;
        this.feature.gradFill.value.presetItem = null;
        this.feature.gradFill.confirmGradChoice();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_RADIAL_GRADIENT, 'value')
    public getGradualFillRadial(): boolean {
        return this.feature.gradFill.value.curGradualType === RADIAL_GRADIENT;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_DIRECTION, 'value')
    public getGradualFillDirect(): boolean {
        return this.feature.gradFill.value.curDirect;
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_DIRECTION, 'onChange')
    public changeGradualFillDirect(value) {
        this.feature.gradFill.handleGradualFillDirect(value);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_DIRECTION, 'options')
    public getGradualFillDirectionOptions() {
        return {
            selectOptions: this.feature.getFillDirectionOptions(),
        };
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_ANGLE, 'value')
    public getGradualFillAngle(): number | IMultiDiffValue {
        const gradualAngleObj = { gradualAngle: this.feature.gradFill.value.gradualAngle };
        return this.feature.getShownValue(this.feature.getInputParams().key, gradualAngleObj);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_ANGLE, 'onChange')
    public changeGradualFillAngle(value: number, from: AngleFrom, dragState?: DragAngleCircleType) {
        const { gradualAngle } = this.feature.getValidValue({ gradualAngle: value }, from, ANGLE_MAX);
        const dragEnd = !dragState || dragState === DragAngleCircleType.STOP;
        this.feature.handleAngleChange(gradualAngle % 360, dragEnd, from);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_BAR, 'content')
    public renderGradualFill() {
        return this.feature.renderGradualFill();
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_OPACITY, 'value')
    public getGradualFillOpacity(): number | IMultiDiffValue {
        const gradualOpacityObj = { gradualOpacity: this.feature.gradFill.getGradualOpacity() };
        return this.feature.getShownValue(this.feature.getInputParams().key, gradualOpacityObj);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_OPACITY, 'onChange')
    public changeGradualFillOpacity(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { gradualOpacity } = this.feature.getValidValue({ gradualOpacity: value }, from);
        this.feature.handleGradualOpacityChange(gradualOpacity, from, dragEnd);
    }

    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_ANGLE, 'shouldDisabled')
    @Form(ChartFormItemKey.CHART_GLOBAL_GRADUAL_FILL_ANGLE_TEXT, 'shouldDisabled')
    public shouldAngleDisabled(): boolean {
        return !(this.feature.curFillType === FillType.GRADUAL_FILL
            && this.feature.gradFill.value.curGradualType === LINEAR_GRADIENT);
    }
}
